import { create } from 'zustand'
import type { AppSettings, LoadState } from '@/types'
import { api, describeError } from '@/services/api'

interface SettingsState {
  status: LoadState
  error: string | null
  settings: AppSettings | null

  load: () => Promise<void>
  update: (patch: Partial<AppSettings>) => Promise<void>
}

/**
 * Applies the device's own light/dark preference.
 *
 * There is no in-app switch any more: the OS already has one, people set it
 * once, and a second control that disagrees with it is a setting to maintain
 * rather than a feature.
 */
export function applyTheme(): void {
  const root = document.documentElement
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  const resolved = prefersDark ? 'dark' : 'light'
  root.setAttribute('data-theme', resolved)
  root.style.colorScheme = resolved
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  status: 'idle',
  error: null,
  settings: null,

  async load() {
    set({ status: 'loading' })
    try {
      const settings = await api.get<AppSettings>('/settings')
      applyTheme()
      set({ settings, status: 'success', error: null })
    } catch (error) {
      set({ status: 'error', error: describeError(error) })
    }
  },

  async update(patch) {
    const current = get().settings
    if (current) set({ settings: { ...current, ...patch } })
    try {
      const saved = await api.patch<AppSettings>('/settings', patch)
      set({ settings: saved, error: null })
    } catch (error) {
      set({ error: describeError(error) })
      await get().load()
    }
  },

}))

/** Follows the OS while the app is open, so a scheduled switch takes effect. */
export function initThemeListener(): () => void {
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  if (!media) return () => undefined
  const handler = () => applyTheme()
  media.addEventListener('change', handler)
  return () => media.removeEventListener('change', handler)
}
